import type { WorldViewActivity } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatTime } from "@/lib/format";

/**
 * UI-23's one status line, in words: "Updating the world view · 12 insights · step 2
 * of 3" while a pass is running, "Up to date · last updated 10:32" once one has
 * finished, or null while the read itself hasn't landed yet (the caller's existing
 * loading state covers that gap, the same way it already does for `worldView`).
 */
export function describeWorldViewActivity(activity: WorldViewActivity | null): string | null {
  if (!activity) return null;

  if (activity.running) {
    const { insightCount, stepIndex, stepCount } = activity.running;
    return `Updating the world view · ${insightCount} ${insightCount === 1 ? "insight" : "insights"} · step ${stepIndex} of ${stepCount}`;
  }

  return activity.lastBatch
    ? `Up to date · last updated ${formatTime(activity.lastBatch.completedAt)}`
    : "Up to date";
}

/**
 * The status line rendered, with a small pulsing dot while a pass is running — the
 * same dot-plus-label recipe `StatusPill` (Recordings.tsx) uses for "Processing", kept
 * as plain muted text rather than a pill because it shares the header's plain line
 * (`{total} subjects · last rewritten …`).
 */
export function WorldViewActivityLine({
  activity,
  className,
}: {
  activity: WorldViewActivity | null;
  className?: string;
}) {
  const text = describeWorldViewActivity(activity);
  if (!text) return null;

  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      {activity?.running && (
        <span
          className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-[var(--radius-full)]"
          style={{ background: "var(--vl-bridge)" }}
          aria-hidden
        />
      )}
      {text}
    </span>
  );
}
